import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AnalyticsService, ToastService } from '@common/services';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { PaymentMobileFormData, ReferralCodeData } from '../../models/referral-coupon.model';
import { ReferralService } from '../../services/referral.service';

const DEFAULT_REFERRAL_REWARD = 10;

const ANALYTICS_EVENTS = {
    SHARE_WHATSAPP: (source: string) => `${source}_referralWhatsapp button`,
    MODAL_SHOWN: 'referral_modal_shown',
    MODAL_SHARE_CLICKED: 'referral_modal_share_clicked',
    MODAL_COPY_LINK: 'referral_modal_copy_link',
};

type AnalyticsSource = 'login' | 'sidenav';
type AnalyticsOrigin = 'login' | 'web';

const ORIGIN_BY_SOURCE: Record<AnalyticsSource, AnalyticsOrigin> = {
    login: 'login',
    sidenav: 'web',
};

@Component({
    selector: 'app-referral-panel',
    templateUrl: './referral-panel.component.html',
    styleUrls: ['./referral-panel.component.scss'],
})
export class ReferralPanelComponent implements OnChanges, OnDestroy {
    /** tenantId del comercio que genera/comparte el código. Si llega `null`, el panel no se renderiza. */
    @Input() tenantId: string | null = null;

    /** Origen del consumo, usado en eventos de analytics para distinguir login vs sidenav. */
    @Input() analyticsSource: AnalyticsSource = 'login';

    readonly defaultReward = DEFAULT_REFERRAL_REWARD;

    isLoading = false;
    referralCodeData: ReferralCodeData | null = null;
    referralLink = '';

    isEditingPayment = false;
    isSavingPayment = false;
    paymentSaveError: string | null = null;
    paymentForm: PaymentMobileFormData = { cedula: '', phone: '', bank: '' };

    linkCopied = false;

    private destroy$ = new Subject<void>();
    private hasReportedShown = false;
    private copyResetTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(private referralService: ReferralService, private analyticsService: AnalyticsService, private toastService: ToastService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tenantId']) {
            this.loadReferralInfo();
            this.reportShownIfNeeded();
        }
    }

    private get origin(): AnalyticsOrigin {
        return ORIGIN_BY_SOURCE[this.analyticsSource];
    }

    private reportShownIfNeeded(): void {
        if (this.hasReportedShown || !this.tenantId) return;
        this.hasReportedShown = true;
        this.analyticsService.analyticsCapture(ANALYTICS_EVENTS.MODAL_SHOWN, { origin: this.origin });
    }

    ngOnDestroy(): void {
        if (this.copyResetTimeout) {
            clearTimeout(this.copyResetTimeout);
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadReferralInfo(): void {
        this.referralCodeData = null;
        this.referralLink = '';
        this.isEditingPayment = false;
        this.linkCopied = false;

        if (!this.tenantId) return;

        // En el contexto de login el usuario no está autenticado: usar el endpoint público.
        // En el contexto de sidenav el usuario ya tiene JWT: usar el endpoint privado que
        // devuelve paymentMode, paymentMobile y refereeDiscount completos.
        const request$ = this.analyticsSource === 'login' ? this.referralService.getReferralCode(this.tenantId) : this.referralService.getReferralInfo();

        this.isLoading = true;
        request$
            .pipe(
                finalize(() => (this.isLoading = false)),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: data => {
                    this.referralCodeData = data;
                    this.referralLink = this.buildReferralLink(data.code);
                },
                error: () => {},
            });
    }

    private buildReferralLink(code: string): string {
        const base = environment.selfRegistrationUrl;
        const separator = base.includes('?') ? '&' : '?';
        return `${base}${separator}referredBy=${encodeURIComponent(code)}`;
    }

    get hasPaymentMobile(): boolean {
        return !!this.referralCodeData?.paymentMobile?.cedula;
    }

    startEditPayment(): void {
        const pm = this.referralCodeData?.paymentMobile;
        this.paymentForm = {
            cedula: pm?.cedula ?? '',
            phone: pm?.phone ?? '',
            bank: pm?.bank ?? '',
        };
        this.paymentSaveError = null;
        this.isEditingPayment = true;
    }

    cancelEditPayment(): void {
        this.isEditingPayment = false;
        this.paymentSaveError = null;
    }

    onSavePaymentMobile(): void {
        this.paymentSaveError = null;
        const { cedula, phone, bank } = this.paymentForm;

        const cedulaDigits = cedula.replace(/\D/g, '');
        const phoneDigits = phone.replace(/\D/g, '');

        if (!cedulaDigits || !phoneDigits || !bank.trim()) {
            this.paymentSaveError = 'Todos los campos son requeridos.';
            return;
        }
        if (cedulaDigits.length < 6 || cedulaDigits.length > 12) {
            this.paymentSaveError = 'La cédula debe tener entre 6 y 12 dígitos.';
            return;
        }
        if (phoneDigits.length < 10 || phoneDigits.length > 12) {
            this.paymentSaveError = 'El teléfono debe tener entre 10 y 12 dígitos.';
            return;
        }
        if (bank.trim().length > 100) {
            this.paymentSaveError = 'El nombre del banco no puede exceder 100 caracteres.';
            return;
        }

        const payload: PaymentMobileFormData = { cedula: cedulaDigits, phone: phoneDigits, bank: bank.trim() };

        this.isSavingPayment = true;
        this.referralService
            .savePaymentMobile(payload)
            .pipe(
                finalize(() => (this.isSavingPayment = false)),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: result => {
                    if (this.referralCodeData) {
                        this.referralCodeData = { ...this.referralCodeData, paymentMobile: result.paymentMobile };
                    }
                    this.isEditingPayment = false;
                    this.toastService.success('Datos guardados', 'Tus datos de pago móvil fueron guardados correctamente.');
                },
                error: () => {
                    this.paymentSaveError = 'No pudimos guardar tus datos. Intenta de nuevo.';
                },
            });
    }

    shareOnWhatsApp(): void {
        if (!this.referralLink) return;

        this.analyticsService.analyticsCapture(ANALYTICS_EVENTS.SHARE_WHATSAPP(this.analyticsSource));
        this.analyticsService.analyticsCapture(ANALYTICS_EVENTS.MODAL_SHARE_CLICKED, { origin: this.origin });

        const line1 = '👀 ¡Hola! Te comparto esto porque sé que puede servirte…';
        const line2 = 'Yo estoy usando Fina, una plataforma para llevar el control de mi negocio (ventas, gastos, inventario y cuentas) sin complicaciones.';
        const line3 = 'Empieza con este enlace 👇';
        const line4 = `🔗 ${this.referralLink}`;
        const line5 = 'Súper recomendada si quieres dejar el Excel o el cuaderno y tener todo en un solo lugar.';

        const fullMessage = [line1, line2, line3, line4, line5].join('\n\n');
        const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(fullMessage)}`;
        window.open(whatsappUrl, '_blank');
    }

    copyReferralLink(): void {
        if (!this.referralLink) return;

        this.analyticsService.analyticsCapture(ANALYTICS_EVENTS.MODAL_COPY_LINK, { origin: this.origin });

        const onCopied = () => {
            this.linkCopied = true;
            if (this.copyResetTimeout) {
                clearTimeout(this.copyResetTimeout);
            }
            this.copyResetTimeout = setTimeout(() => (this.linkCopied = false), 2000);
        };

        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(this.referralLink).then(onCopied, () => this.fallbackCopy(this.referralLink, onCopied));
        } else {
            this.fallbackCopy(this.referralLink, onCopied);
        }
    }

    private fallbackCopy(text: string, onCopied: () => void): void {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
            document.execCommand('copy');
            onCopied();
        } catch {
            this.toastService.error('No se pudo copiar', 'Copia el enlace manualmente.');
        } finally {
            document.body.removeChild(textarea);
        }
    }
}
